import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  FolderIcon,
  FolderPlusIcon,
  RectangleStackIcon,
  PlayCircleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CalendarIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { FolderIcon as FolderIconSolid } from '@heroicons/react/24/solid';
import { projectsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import CreateProjectModal from '../components/CreateProjectModal';
import PaginationBar from '../components/PaginationBar';
import { itemCountLabel, runCountLabel, standardLabel } from '../utils/labels';

// Debounce delay for the search box (D-decision, no plan-mandated value): keeps a
// keystroke from firing a request per character.
const SEARCH_DEBOUNCE_MS = 300;

// project.status is one of ACTIVE | ARCHIVED | SUSPENDED (backend/app/models/project.py
// ProjectStatus); mirrors the main app's badge/filter set (6.3 recipes).
const STATUS_BADGES = {
  ACTIVE: { color: 'bg-green-100 text-green-800', label: 'Active' },
  ARCHIVED: { color: 'bg-[#F8F8FF] text-gray-800', label: 'Archived' },
  SUSPENDED: { color: 'bg-red-100 text-red-800', label: 'Suspended' },
};

function StatusBadge({ status }) {
  const config = STATUS_BADGES[status] || STATUS_BADGES.ACTIVE;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '';
  // The API sends naive, offset-less timestamps (the models store datetime.utcnow), and
  // ECMAScript reads a date-time string with no offset as LOCAL time - so west of UTC this
  // rendered the previous day. Append Z unless the string already carries an offset.
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  const parsed = new Date(hasOffset ? value : `${value}Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// "{n} videos / {n} runs" (19.3, 19.5 client bullets, and the 19.6 test table row) - this
// project's content units, not the main app's documents/analyses. QA 2026-09-29 read "1 videos
// / 1 runs"; the counts now take the singular at one and name photo sets too.
function activityLabel(project) {
  return `${itemCountLabel(project.video_count)} / ${runCountLabel(project.run_count)}`;
}

function truncate(text, maxLength = 60) {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' (default, 19.3) or 'cards'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Debounce the raw input into the value the query actually uses.
  useEffect(() => {
    const handle = setTimeout(() => setSearchTerm(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, pageSize]);

  const { data, isLoading, isError, error, refetch, isFetching, isPreviousData } = useQuery(
    ['projects', { search: searchTerm, status: statusFilter, page, pageSize }],
    () => projectsAPI.list({
      search: searchTerm || undefined,
      status: statusFilter || undefined,
      skip: (page - 1) * pageSize,
      limit: pageSize,
    }),
    {
      select: (response) => response.data || {},
      keepPreviousData: true,
    }
  );

  const projects = data?.projects || [];
  const total = data?.total ?? 0;
  // While a new search or filter is on its way (the debounce, then the request), the list on
  // screen is the previous one: say so instead of leaving it looking current (QA 2026-09-29).
  const searching = searchInput.trim() !== searchTerm || (isFetching && isPreviousData);

  const handleRowClick = (project) => navigate(`/projects/${project.id}`);

  if (isLoading) {
    return (
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse" data-testid="projects-loading">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6" />
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <FolderIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading projects</h3>
          <p className="mt-1 text-sm text-gray-500">{getApiErrorMessage(error, 'Something went wrong')}</p>
          <div className="mt-6">
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
            <p className="mt-1 text-sm text-gray-500">
              Organize your videos, photos and compliance runs by certification standard
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FolderPlusIcon className="h-4 w-4 mr-2" />
            Create Project
          </button>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search projects"
              className="w-full pl-10 pr-28 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div role="status" className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-xs text-gray-500">
              {searching && (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" aria-hidden="true" />
                  Searching…
                </>
              )}
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="ARCHIVED">Archived</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </div>
      </div>

      <div aria-busy={searching} className={searching ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
      {total === 0 ? (
        <div className="text-center py-12">
          <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No projects found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating your first project.'}
          </p>
          {!searchTerm && !statusFilter && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <FolderPlusIcon className="h-4 w-4 mr-2" />
                Create Your First Project
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">
              {total} {total === 1 ? 'Project' : 'Projects'}
            </h2>
            <div className="flex items-center bg-[#F8F8FF] rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                aria-label="Table view"
                aria-pressed={viewMode === 'table'}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'table' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Table View"
              >
                <ListBulletIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                aria-label="Card view"
                aria-pressed={viewMode === 'cards'}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'cards' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Card View"
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {viewMode === 'cards' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => handleRowClick(project)}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center min-w-0">
                        <FolderIconSolid className="h-8 w-8 text-blue-500 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <h3
                            className="text-lg font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate"
                            title={project.name}
                          >
                            {project.name}
                          </h3>
                          <div className="mt-1">
                            <StatusBadge status={project.status} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {project.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                    )}

                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <ShieldCheckIcon className="h-4 w-4 mr-2" />
                      <span className="truncate">{standardLabel(project.standard_name, project.standard_version)}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <RectangleStackIcon className="h-4 w-4 mr-1" />
                        <span>{itemCountLabel(project.video_count)}</span>
                      </div>
                      <div className="flex items-center">
                        <PlayCircleIcon className="h-4 w-4 mr-1" />
                        <span>{runCountLabel(project.run_count)}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-400 pt-4 border-t border-gray-100">
                      <div className="flex items-center">
                        <CalendarIcon className="h-3 w-3 mr-1" />
                        <span>Created {formatDate(project.created_at)}</span>
                      </div>
                      <div>Last activity {formatDate(project.latest_activity)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[#F8F8FF]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standard</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {projects.map((project) => (
                      <tr
                        key={project.id}
                        className="hover:bg-[#F8F8FF] cursor-pointer"
                        onClick={() => handleRowClick(project)}
                      >
                        <td className="px-6 py-4 max-w-xs">
                          <div className="flex items-start min-w-0">
                            <FolderIconSolid className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <span className="text-sm font-medium text-gray-900 truncate block" title={project.name}>
                                {project.name}
                              </span>
                              {project.description && (
                                <div className="text-xs text-gray-500 mt-1 line-clamp-1">
                                  {truncate(project.description)}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <ShieldCheckIcon className="h-4 w-4 mr-2 text-gray-400" />
                            {standardLabel(project.standard_name, project.standard_version)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {activityLabel(project)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={project.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {project.creator_first_name && project.creator_last_name
                              ? `${project.creator_first_name} ${project.creator_last_name}`
                              : 'Unknown User'}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {formatDate(project.created_at)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <PaginationBar
            className="mt-6 bg-white rounded-lg border border-gray-200 px-6 py-4"
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            pageSizeOptions={[25, 50, 100]}
            itemLabel="projects"
          />
        </>
      )}
      </div>

      <CreateProjectModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />
    </div>
  );
}
